"use client";

import { useState, useEffect } from "react";
import api, { GitHubRepo, Integration } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatTimeAgo } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Loader2,
  Github,
  GitlabIcon,
  Globe,
  ArrowRight,
  ArrowLeft,
  Lock,
  Search,
  Link2,
} from "lucide-react";

interface AddAppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const PROJECT_TYPES = [
  { value: "flutter", label: "Flutter (iOS)" },
  { value: "flutter_android", label: "Flutter (Android)" },
  { value: "ios_native", label: "iOS Native" },
  { value: "android_native", label: "Android Native" },
  { value: "react_native", label: "React Native (iOS)" },
  { value: "react_native_android", label: "React Native (Android)" },
];

// Android builds run in Docker on Linux and are signed with a keystore, so the iOS
// bundle ID is not used for them.
const ANDROID_PROJECT_TYPES = [
  "flutter_android",
  "android_native",
  "react_native_android",
];

const GIT_PROVIDERS = [
  { value: "github", label: "GitHub", icon: Github },
  { value: "gitlab", label: "GitLab", icon: GitlabIcon },
  { value: "other", label: "Other", icon: Globe },
];

export function AddAppDialog({
  open,
  onOpenChange,
  onSuccess,
}: AddAppDialogProps) {
  const { currentTeam } = useAuth();
  const [step, setStep] = useState(1);
  const [gitProvider, setGitProvider] = useState("");
  const [name, setName] = useState("");
  const [repoUrl, setRepoUrl] = useState("");
  const [projectType, setProjectType] = useState("");
  const [projectPath, setProjectPath] = useState("");
  const [bundleId, setBundleId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // GitHub repo listing state
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [repos, setRepos] = useState<GitHubRepo[]>([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [repoSearch, setRepoSearch] = useState("");
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepo | null>(null);

  const providerConnected = (provider: string) =>
    integrations.some((i) => i.provider === provider && i.connected);

  // Load integrations when dialog opens
  useEffect(() => {
    if (!open) return;
    api.getIntegrations().then((r) => setIntegrations(r || [])).catch(() => {});
  }, [open]);

  // Load repos when a supported provider is selected and connected
  useEffect(() => {
    if (step !== 2 || gitProvider === "other" || !providerConnected(gitProvider)) return;
    setReposLoading(true);
    api
      .getReposByProvider(gitProvider)
      .then((r) => setRepos(r || []))
      .catch(() => setRepos([]))
      .finally(() => setReposLoading(false));
  }, [step, gitProvider, integrations]);

  function resetForm() {
    setStep(1);
    setGitProvider("");
    setName("");
    setRepoUrl("");
    setProjectType("");
    setProjectPath("");
    setBundleId("");
    setError(null);
    setSelectedRepo(null);
    setRepoSearch("");
    setRepos([]);
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  }

  function handleSelectRepo(repo: GitHubRepo) {
    setSelectedRepo(repo);
    setName(repo.name);
    setRepoUrl(repo.clone_url);
  }

  async function handleSubmit() {
    if (!currentTeam) return;
    if (!name.trim() || !repoUrl.trim() || !projectType || !gitProvider) return;

    setSubmitting(true);
    setError(null);

    try {
      await api.createApp(currentTeam.id, {
        name: name.trim(),
        repo_url: repoUrl.trim(),
        project_type: projectType,
        project_path: projectPath.trim() || undefined,
        git_provider: gitProvider === "other" ? "github" : gitProvider,
        bundle_id: bundleId.trim() || undefined,
      });
      handleOpenChange(false);
      onSuccess();
    } catch (err: any) {
      setError(err.message || "Failed to create application");
    } finally {
      setSubmitting(false);
    }
  }

  const filteredRepos = repoSearch
    ? repos.filter((r) =>
        r.full_name.toLowerCase().includes(repoSearch.toLowerCase())
      )
    : repos;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Application</DialogTitle>
          <DialogDescription>
            {step === 1
              ? "Select your repository provider to get started."
              : step === 2
              ? gitProvider !== "other" && providerConnected(gitProvider)
                ? `Select a repository from your ${GIT_PROVIDERS.find(p => p.value === gitProvider)?.label || gitProvider} account.`
                : "Enter your repository details."
              : "Select your project type."}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 px-1">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-1 flex-1 rounded-full transition-colors ${
                s <= step ? "bg-violet-500" : "bg-muted"
              }`}
            />
          ))}
        </div>

        {/* Step 1: Choose provider */}
        {step === 1 && (
          <div className="grid grid-cols-2 gap-3 py-2">
            {GIT_PROVIDERS.map((provider) => {
              const Icon = provider.icon;
              const selected = gitProvider === provider.value;
              return (
                <button
                  key={provider.value}
                  onClick={() => setGitProvider(provider.value)}
                  className={`flex flex-col items-center gap-2 rounded-lg border-2 p-6 transition-colors ${
                    selected
                      ? "border-violet-500 bg-violet-500/5"
                      : "border-border hover:border-muted-foreground/30 hover:bg-muted/50"
                  }`}
                >
                  <Icon className="size-8 text-muted-foreground" />
                  <span className="text-sm font-medium">{provider.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Step 2: Select or enter repo */}
        {step === 2 && (
          <div className="space-y-4 py-2">
            {gitProvider !== "other" && !providerConnected(gitProvider) ? (
              /* Provider not connected - show connect prompt */
              <div className="flex flex-col items-center gap-4 rounded-lg border border-dashed py-8">
                <div className="flex size-12 items-center justify-center rounded-full bg-muted">
                  <Link2 className="size-6 text-muted-foreground" />
                </div>
                <div className="text-center">
                  <p className="text-sm font-medium">{GIT_PROVIDERS.find(p => p.value === gitProvider)?.label || gitProvider} not connected</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Connect your {GIT_PROVIDERS.find(p => p.value === gitProvider)?.label || gitProvider} account to browse your repositories.
                  </p>
                </div>
                <Button asChild variant="outline" size="sm">
                  <a href={api.getIntegrationUrl(gitProvider)}>
                    {(() => { const Icon = GIT_PROVIDERS.find(p => p.value === gitProvider)?.icon || Globe; return <Icon className="size-4" />; })()}
                    Connect {GIT_PROVIDERS.find(p => p.value === gitProvider)?.label || gitProvider}
                  </a>
                </Button>
                <button
                  onClick={() => {/* allow manual fallback */}}
                  className="text-xs text-muted-foreground underline"
                >
                  Or enter a repository URL manually
                </button>
              </div>
            ) : gitProvider !== "other" && providerConnected(gitProvider) ? (
              /* Provider connected - show repo list */
              <div className="space-y-3">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search repositories..."
                    value={repoSearch}
                    onChange={(e) => setRepoSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>

                {/* Repo list */}
                <div className="max-h-64 overflow-y-auto rounded-lg border">
                  {reposLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="size-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredRepos.length === 0 ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">
                      {repoSearch
                        ? "No repositories match your search."
                        : "No repositories found."}
                    </div>
                  ) : (
                    filteredRepos.map((repo) => {
                      const ProviderIcon = GIT_PROVIDERS.find(p => p.value === gitProvider)?.icon || Globe;
                      return (
                        <button
                          key={repo.id || repo.full_name}
                          onClick={() => handleSelectRepo(repo)}
                          className={`flex w-full items-center gap-3 border-b px-4 py-3 text-left transition-colors last:border-b-0 ${
                            selectedRepo?.full_name === repo.full_name
                              ? "bg-violet-500/10"
                              : "hover:bg-muted/50"
                          }`}
                        >
                          <ProviderIcon className="size-4 shrink-0 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="truncate text-sm font-medium">
                                {repo.full_name}
                              </span>
                              {repo.private && (
                                <Lock className="size-3 shrink-0 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground">
                              {repo.language && <span>{repo.language}</span>}
                              {repo.updated_at && <span>Updated {formatTimeAgo(repo.updated_at)}</span>}
                            </div>
                          </div>
                        </button>
                      );
                    })
                  )}
                </div>

                {/* Selected repo name override */}
                {selectedRepo && (
                  <div className="space-y-2">
                    <Label htmlFor="app-name">App Name</Label>
                    <Input
                      id="app-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="My Awesome App"
                    />
                  </div>
                )}
              </div>
            ) : (
              /* Other providers - manual entry */
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="app-name">App Name</Label>
                  <Input
                    id="app-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="My Awesome App"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="repo-url">Repository URL</Label>
                  <Input
                    id="repo-url"
                    value={repoUrl}
                    onChange={(e) => setRepoUrl(e.target.value)}
                    placeholder="https://github.com/owner/repo"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Project type */}
        {step === 3 && (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="project-type">Project Type</Label>
              <Select value={projectType} onValueChange={setProjectType}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select project type" />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="project-path">Project path (optional)</Label>
              <Input
                id="project-path"
                value={projectPath}
                onChange={(e) => setProjectPath(e.target.value)}
                placeholder="apps/mobile"
              />
              <p className="text-xs text-muted-foreground">
                Subdirectory holding the app, for monorepos. Leave empty if the app is at
                the repository root.
              </p>
            </div>

            {!ANDROID_PROJECT_TYPES.includes(projectType) && (
              <div className="space-y-2">
                <Label htmlFor="bundle-id">Bundle ID (iOS)</Label>
                <Input
                  id="bundle-id"
                  value={bundleId}
                  onChange={(e) => setBundleId(e.target.value)}
                  placeholder="com.example.app"
                />
                <p className="text-xs text-muted-foreground">
                  Required for iOS code signing. Matches provisioning profiles automatically.
                </p>
              </div>
            )}

            {ANDROID_PROJECT_TYPES.includes(projectType) && (
              <p className="text-xs text-muted-foreground">
                Android builds are signed with the keystore configured in your team&apos;s
                code signing settings.
              </p>
            )}

            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
        )}

        <DialogFooter className="gap-2">
          {step > 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setStep(step - 1);
                if (step === 2) {
                  setSelectedRepo(null);
                  setRepoSearch("");
                }
              }}
              disabled={submitting}
            >
              <ArrowLeft className="size-4" />
              Back
            </Button>
          )}
          {step === 1 && (
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancel
            </Button>
          )}

          {step < 3 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={
                (step === 1 && !gitProvider) ||
                (step === 2 && (!name.trim() || !repoUrl.trim()))
              }
            >
              Next
              <ArrowRight className="size-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !projectType}
            >
              {submitting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : null}
              Add Application
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
